import ServiceWorkOrders from '../components/service-work-orders/ServiceWorkOrders';
import Messages from '../components/messaging/Messages';
import ResourceLibrary, { LegacyAcademyEntry } from '../components/utilities/ResourceLibrary';
import NewHire from '../components/onboarding/NewHire';
import NewHireSettings from '../components/onboarding/NewHireSettings';
import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useLocation, withRouter } from 'react-router';
import Admin from '../components/admin';
import CachedResponse from '../components/common/CachedResponse';
import VideoPlayer from '../components/common/VideoPlayer';
import CRM from '../components/crm';
import DocHub from '../components/doc-hub/DocHub';
import GlobalSearch from '../components/globalSearch/index';
import Header from '../components/Header';
import Installer from '../components/installer/Installer';
import Inventory from '../components/inventory';
import Contract from '../components/main';
import NotFound from '../components/NotFound';
import Ops from '../components/ops/Ops';
import PipelineRoute from '../components/pipeline';
import Profile from '../components/profile';
import Reports from '../components/reports/Reports';
import Requests from '../components/requests/Requests';
import Schedule from '../components/schedule/Schedule';
import Utilities from '../components/utilities/Utilities';
import { findSpecificSectionVideo } from '../helpers/global';
import { getDefaultRouteForUser } from '../helpers/roleHelpers';
import useKeypress from '../hooks/KeyPressHook';
import SalesLocations from '../components/admin/SalesLocation';
import SiteMap from '../components/inventory/SiteMap';
import CourseTrainingIndex from '../components/admin/courseTraining/CourseTrainingIndex';
import ConversationBank from '../components/conversation-bank';
import TopProducerView from '../components/top-producer/TopProducerView';

const Home = ({ appChoices }) => {
  const location = useLocation();
  const [currentSectionVideoMeta, setCurrentSectionVideoMeta] = useState({});
  const [openVideoplayer, setOpenVideoPlayer] = useState(null);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [globalSearchResults, setGlobalSearchResults] = useState(null);

  const onShowGlobalSearchValueChange = value => {
    setShowGlobalSearch(value);
    if (value === false) {
      setGlobalSearchResults(null);
    }
  };
  const onOpenVideoPlayerChange = value => {
    setOpenVideoPlayer(value);
  };

  const onOpenVideoPlayerClose = () => {
    setOpenVideoPlayer(null);
  };

  const featuredVideoMeta = useMemo(() => {
    const videoUrls = appChoices
      ?.find(appChoice => appChoice.key === 'sectionVideos')
      ?.values?.find(videoData => videoData?.name === 'Featured Videos');

    return videoUrls;
  }, [location.pathname, appChoices]);

  useEffect(() => {
    const videoUrls = appChoices.find(appChoice => appChoice.key === 'sectionVideos');
    setCurrentSectionVideoMeta(findSpecificSectionVideo(videoUrls?.values, location.pathname));
  }, [location.pathname, appChoices]);

  // If two keys are clicked simultanously the first key must be [ctrl,alt,shift] key

  useKeypress(['ctrl', 'q'], () => {
    onShowGlobalSearchValueChange(true);
  });

  if (location.pathname === '/') {
    return <Redirect from="/" to={getDefaultRouteForUser()} />;
  }

  return (
    <>
      <GlobalSearch
        onShowGlobalSearchValueChange={onShowGlobalSearchValueChange}
        showGlobalSearch={showGlobalSearch}
        globalSearchResults={globalSearchResults}
        onGlobalSearchResultsChange={setGlobalSearchResults}
      />
 
      <Header
        currentSectionVideoMeta={currentSectionVideoMeta}
        featuredVideoMeta={featuredVideoMeta}
        onOpenVideoPlayerChange={onOpenVideoPlayerChange}
      />

      {openVideoplayer && (
        <VideoPlayer
          videoLinks={openVideoplayer === 'section' ? currentSectionVideoMeta?.videoUrls : featuredVideoMeta?.videoUrls}
          title={openVideoplayer === 'section' ? currentSectionVideoMeta?.name : 'About This App'}
          onPlayerClose={onOpenVideoPlayerClose}
          openVideoplayer={openVideoplayer}
        />
      )}

      <div style={{ paddingTop: 56 }}>
        <Switch>
          <Route path="/messages"><Messages /></Route>
          <Route exact path="/onboarding/settings"><NewHireSettings /></Route>
          <Route exact path="/onboarding"><NewHire /></Route>
          <Route exact path="/profile">
            <Profile />
          </Route>
          <Route exact path="/service-work-orders"><ServiceWorkOrders /></Route>
          <Route path="/contract">
            <Contract />
          </Route>
          <Route path="/service-requests">
            <Requests />
          </Route>
          <Route path="/dochub">
            <DocHub />
          </Route>
          <Route path="/crm">
            <CRM />
          </Route>
          <Route exact path="/top-producer-view">
            <TopProducerView />
          </Route>
          <Route path="/conversation-bank">
            <ConversationBank />
          </Route>
          <Route path="/inventory">
            <Inventory />
          </Route>
          <Route path="/walkthrough">
            <Ops />
          </Route>

          <Route path="/setup">
            <Installer />
          </Route>
          <Route path="/reports">
            <Reports />
          </Route>
          {/* View This */}
          <Route path="/schedule">
            <CachedResponse listEndpoint={'app-choices'} render={a => <Schedule appChoices={a} />} />
          </Route>
          <Route path="/pipeline">
            <PipelineRoute />
          </Route>
          <Route path="/utilities">
            <Utilities />
          </Route>
          <Route
            path="/mhc-sales-development-system/:courseId?"
            render={({ match, location }) => (
              <Redirect
                to={{
                  pathname: match.params.courseId
                    ? `/sales-academy/${match.params.courseId}`
                    : '/sales-academy',
                  search: location.search,
                  hash: location.hash
                }}
              />
            )}
          />
          <Route exact path="/sales-academy/:courseId">
            <CourseTrainingIndex />
          </Route>
          <Route exact path="/training-resources"><ResourceLibrary /></Route>
          <Route exact path="/sales-academy">
            <LegacyAcademyEntry />
          </Route>
          <Route path="/admin">
            <Admin />
          </Route>
          <Route path="*">
            <NotFound />
          </Route>
        </Switch>
      </div>
    </>
  );
};

export default withRouter(Home);
